/**
 * Pure Rang card-play engine for the online game: shuffle and deal, choosing
 * Rang (trump), following suit, winning tricks, and whether the Rang team
 * "runs" (saves itself). No DOM, storage or network — the Durable Object runs
 * it authoritatively and the tests run it directly.
 *
 * House rules implemented here:
 *  - The caller (the seat after the dealer) gets 5 cards and chooses Rang
 *    before anyone else is dealt; then 5 each to the rest, then 4 + 4 each.
 *  - The caller leads the first trick. Follow the led suit if you can.
 *    Highest Rang card wins; otherwise the highest card of the led suit.
 *  - Only the team that chose Rang can run; the dealing team's job is to stop them.
 *  - Tricks 1–3 never count towards a run. From trick 4 on, the Rang team is
 *    saved when one of its players wins two tricks in a row (wins one, leads
 *    the next, and wins that too). A partner winning the next one is not a run.
 *    The dealing team winning tricks in a row means nothing — it only breaks the streak.
 *  - With no run, the Rang team is still saved by winning trick 13; otherwise it gets Guaa.
 *  - If all of the caller's first 5 cards are below 10, they may ask for a redeal.
 *  - Once everyone has 13 cards, the dealer decides to play or call off the deal
 *    (too few Rang cards to stop a run). Calling it off counts as a save for the Rang team.
 *  - Mid-hand, either player of the dealing team may also call it off when they
 *    can't stop the run any more — same result: the Rang team is saved.
 */
import {
  ALL_SEATS,
  SUITS,
  TRICKS_PER_DEAL,
  cryptoRandomInt,
  nextSeat,
  shuffle,
  teamOfSeat,
  RuleError,
  type CourtRules,
  type RandomInt,
  type Seat,
  type Suit,
  type TeamId,
  type TurnDirection,
} from './match';

export const RANKS = '23456789TJQKA';
/** Two characters: rank then suit, e.g. "AS" (ace of spades), "TH" (ten of hearts). */
export type Card = string;

/** Tricks before this one are mandatory play and never start a run. */
export const RUN_FROM_TRICK = 4;
export const FIRST_PACKET = 5;

export const cardSuit = (c: Card) => c[1] as Suit;
export const cardRank = (c: Card) => RANKS.indexOf(c[0]!) + 2;
export const rankLabel = (c: Card) => (c[0] === 'T' ? '10' : c[0]!);
export const isCard = (c: unknown): c is Card =>
  typeof c === 'string' && c.length === 2 && RANKS.includes(c[0]!) && (SUITS as readonly string[]).includes(c[1]!);

export function newDeck(): Card[] {
  return SUITS.flatMap((s) => [...RANKS].map((r) => r + s));
}

/** Seat receiving each of the 52 cards, in dealing order. */
export function dealSequence(caller: Seat, direction: TurnDirection): Seat[] {
  const order = [0, 1, 2, 3].map((i) => nextSeat(caller, direction, i));
  const out: Seat[] = [];
  const packet = (seat: Seat, n: number) => {
    for (let i = 0; i < n; i++) out.push(seat);
  };
  for (const s of order) packet(s, FIRST_PACKET);
  for (let round = 0; round < 2; round++) for (const s of order) packet(s, 4);
  return out;
}

export interface PlayedCard {
  seat: Seat;
  card: Card;
  /** Bandh Rang: a Rang-suit card played after Rang was revealed (only those act as trump). */
  live?: boolean;
  /** Bandh Rang: a Rang-team discard while Rang is hidden — shown face down to everyone else. */
  faceDown?: boolean;
}

/** What a face-down card looks like to players who may not see it. */
export const HIDDEN_CARD = 'XX';

export interface Trick {
  leader: Seat;
  plays: PlayedCard[];
  /** Set once all four have played. */
  winner?: Seat;
}

/** `confirming`: all cards are dealt; the dealer decides to play the hand or call it off. */
export type HandPhase = 'choosingTrump' | 'confirming' | 'playing' | 'done';

export interface HandOutcome {
  /** The Rang team saved itself (0 Guaa); otherwise it gets Guaa. */
  saved: boolean;
  /** The Rang team ran (always a save), or — with no run — the 13th trick decided it. */
  by: 'run' | 'lastTrick' | 'calledOff';
  /** Who ran, or who took the last trick. */
  decidedBy: Seat;
  /** The trick (1-based) that decided the hand. */
  atTrick: number;
}

export interface HandState {
  dealer: Seat;
  caller: Seat;
  direction: TurnDirection;
  /** Cards still held by each seat, in the order they were dealt. */
  hands: Card[][];
  trump: Suit | null;
  phase: HandPhase;
  current: Trick;
  tricks: Trick[];
  outcome: HandOutcome | null;
  /** How many times this deal was redealt because the caller's first 5 were all below 10. */
  redeals?: number;
  /** Bandh Rang: the Rang is hidden until revealed; set once it has been (play index it took effect from). */
  bandh?: { revealedAt: number | null };
}

export const rangTeam = (hand: Pick<HandState, 'caller'>): TeamId => teamOfSeat(hand.caller);

export function startHand(dealer: Seat, caller: Seat, direction: TurnDirection, rand: RandomInt = cryptoRandomInt): HandState {
  const deck = shuffle(newDeck(), rand);
  const hands: Card[][] = [[], [], [], []];
  dealSequence(caller, direction).forEach((seat, i) => hands[seat]!.push(deck[i]!));
  return {
    dealer,
    caller,
    direction,
    hands,
    trump: null,
    phase: 'choosingTrump',
    current: { leader: caller, plays: [] },
    tricks: [],
    outcome: null,
  };
}

/** Cards below this rank in all of the caller's first 5 let them ask for a redeal. */
export const REDEAL_BELOW = 10;

export function canAskRedeal(hand: Pick<HandState, 'phase' | 'hands' | 'caller'>): boolean {
  return hand.phase === 'choosingTrump' && hand.hands[hand.caller]!.slice(0, FIRST_PACKET).every((c) => cardRank(c) < REDEAL_BELOW);
}

/** The caller had nothing of 10 or above: the same dealer shuffles and deals again. */
export function redeal(hand: HandState, seat: Seat, rand: RandomInt = cryptoRandomInt): HandState {
  if (seat !== hand.caller) throw new RuleError('Only the player choosing Rang can ask for a redeal.');
  if (!canAskRedeal(hand)) throw new RuleError(`A redeal is only allowed when all 5 cards are below ${REDEAL_BELOW}.`);
  return { ...startHand(hand.dealer, hand.caller, hand.direction, rand), redeals: (hand.redeals ?? 0) + 1 };
}

export function chooseTrump(hand: HandState, seat: Seat, suit: Suit): HandState {
  if (hand.phase !== 'choosingTrump') throw new RuleError('Rang has already been chosen.');
  if (seat !== hand.caller) throw new RuleError('Only the player after the dealer chooses Rang.');
  if (!SUITS.includes(suit)) throw new RuleError('Unknown suit.');
  return { ...hand, trump: suit, phase: 'confirming' };
}

/** The dealer looked at their cards and plays the hand. */
export function confirmPlay(hand: HandState, seat: Seat): HandState {
  requireDealerDecision(hand, seat);
  return { ...hand, phase: 'playing' };
}

/**
 * The dealing team can't stop a run, so it calls off the deal: the Rang team is saved.
 * Before play only the dealer decides; during play either dealing-team player may.
 */
export function callOff(hand: HandState, seat: Seat): HandState {
  if (hand.phase === 'playing') {
    if (teamOfSeat(seat) === rangTeam(hand)) throw new RuleError('Only the dealing team can call off the deal.');
  } else requireDealerDecision(hand, seat);
  return { ...hand, phase: 'done', outcome: { saved: true, by: 'calledOff', decidedBy: seat, atTrick: hand.tricks.length } };
}

/** May this player call off the deal mid-hand? */
export const canCallOff = (hand: Pick<HandState, 'phase' | 'caller'>, seat: Seat | null) =>
  seat !== null && hand.phase === 'playing' && teamOfSeat(seat) !== rangTeam(hand);

function requireDealerDecision(hand: HandState, seat: Seat) {
  if (hand.phase !== 'confirming') throw new RuleError(hand.phase === 'choosingTrump' ? 'Wait until Rang is chosen.' : 'This hand is already decided.');
  if (seat !== hand.dealer) throw new RuleError('Only the dealer decides whether to play this hand.');
}

export function toAct(hand: HandState): Seat | null {
  if (hand.phase !== 'playing') return null;
  return nextSeat(hand.current.leader, hand.direction, hand.current.plays.length);
}

export function ledSuit(trick: Trick): Suit | null {
  const first = trick.plays[0];
  return first ? cardSuit(first.card) : null;
}

export function legalCards(hand: HandState, seat: Seat): Card[] {
  if (toAct(hand) !== seat) return [];
  const held = hand.hands[seat]!;
  const led = ledSuit(hand.current);
  const following = led ? held.filter((c) => cardSuit(c) === led) : [];
  return following.length > 0 ? following : held.slice();
}

/** Does `a` beat the card currently winning the trick? */
function beats(a: Card, best: Card, trump: Suit): boolean {
  if (cardSuit(a) === cardSuit(best)) return cardRank(a) > cardRank(best);
  return cardSuit(a) === trump;
}

export function trickWinner(plays: readonly PlayedCard[], trump: Suit): Seat {
  let best = plays[0]!;
  for (const p of plays.slice(1)) if (beats(p.card, best.card, trump)) best = p;
  return best.seat;
}

/** The card that took a completed trick. */
export const winningCard = (trick: Trick): Card | undefined => trick.plays.find((p) => p.seat === trick.winner)?.card;

/** A run can't be made with an Ace (ekka): winning the second trick with one is just a normal trick. */
export const runsWith = (card: Card | undefined) => !!card && card[0] !== 'A';

/** Has the latest completed trick decided the hand for the Rang team (`team`)? */
export function decide(tricks: readonly Trick[], team: TeamId): HandOutcome | null {
  const k = tricks.length;
  if (k === 0) return null;
  const last = tricks[k - 1]!;
  const winner = last.winner!;
  const rangWon = teamOfSeat(winner) === team;
  // Only the Rang team can run; the dealing team winning in a row just keeps them from it.
  if (rangWon && k - 1 >= RUN_FROM_TRICK && tricks[k - 2]!.winner === winner && runsWith(winningCard(last))) {
    return { saved: true, by: 'run', decidedBy: winner, atTrick: k };
  }
  if (k === TRICKS_PER_DEAL) return { saved: rangWon, by: 'lastTrick', decidedBy: winner, atTrick: k };
  return null;
}

export function playCard(hand: HandState, seat: Seat, card: Card): HandState {
  if (hand.phase !== 'playing') {
    throw new RuleError(hand.phase === 'done' ? 'This hand is over.' : hand.phase === 'confirming' ? 'Wait for the dealer to decide whether to play.' : 'Wait until Rang is chosen.');
  }
  if (toAct(hand) !== seat) throw new RuleError("It isn't your turn.");
  if (!hand.hands[seat]!.includes(card)) throw new RuleError("You don't have that card.");
  if (!legalCards(hand, seat).includes(card)) throw new RuleError('You must follow the suit that was led.');

  const hands = hand.hands.map((h, s) => (s === seat ? h.filter((c) => c !== card) : h));
  const plays = [...hand.current.plays, { seat, card }];
  if (plays.length < 4) return { ...hand, hands, current: { ...hand.current, plays } };

  const winner = trickWinner(plays, hand.trump!);
  const tricks = [...hand.tricks, { leader: hand.current.leader, plays, winner }];
  const outcome = decide(tricks, rangTeam(hand));
  return {
    ...hand,
    hands,
    tricks,
    current: { leader: winner, plays: [] },
    outcome,
    phase: outcome ? 'done' : 'playing',
  };
}

export function tricksWon(hand: Pick<HandState, 'tricks'>, team: TeamId): number {
  return hand.tricks.filter((t) => teamOfSeat(t.winner!) === team).length;
}

/** Was a Guaa hand also a Court under these rules? */
export function isCourt(hand: HandState, court: CourtRules): boolean {
  if (!court.enabled || !hand.outcome || hand.outcome.saved) return false;
  const team = rangTeam(hand);
  if (court.mode === 'all13') return hand.tricks.length === TRICKS_PER_DEAL && tricksWon(hand, team) === 0;
  return hand.tricks.length >= 7 && hand.tricks.slice(0, 7).every((t) => teamOfSeat(t.winner!) !== team);
}

/** A Rang-team player who won the latest trick and saves their team by winning the next one too. */
export function runChance(hand: Pick<HandState, 'phase' | 'tricks' | 'caller'>): Seat | null {
  if (hand.phase !== 'playing') return null;
  const last = hand.tricks.at(-1);
  if (!last || hand.tricks.length < RUN_FROM_TRICK) return null;
  return teamOfSeat(last.winner!) === rangTeam(hand) ? last.winner! : null;
}

const isRed = (s: Suit) => s === 'H' || s === 'D';

/**
 * Sort for display: Rang suit first, then suits alternating colour (black, red,
 * black, red — or the reverse) so neighbouring suits never look alike; high cards
 * first within a suit. Only suits actually held count, so a missing suit doesn't
 * leave two same-coloured suits side by side.
 */
export function sortHand(cards: readonly Card[], trump: Suit | null): Card[] {
  const held = SUITS.filter((s) => cards.some((c) => cardSuit(c) === s));
  const order: Suit[] = [];
  let next: Suit | undefined = trump && held.includes(trump) ? trump : held[0];
  while (next) {
    order.push(next);
    const last = next;
    const left = held.filter((s) => !order.includes(s));
    next = left.find((s) => isRed(s) !== isRed(last)) ?? left[0];
  }
  return cards.slice().sort((a, b) => order.indexOf(cardSuit(a)) - order.indexOf(cardSuit(b)) || cardRank(b) - cardRank(a));
}

// ---------------------------------------------------------------------------
// What one player is allowed to see
// ---------------------------------------------------------------------------

export interface HandView {
  dealer: Seat;
  caller: Seat;
  direction: TurnDirection;
  trump: Suit | null;
  phase: HandPhase;
  current: Trick;
  tricks: Trick[];
  outcome: HandOutcome | null;
  /** Cards each seat is visibly holding. Before Rang is chosen only the caller holds any. */
  counts: number[];
  /** The viewer's own cards (empty for spectators, and for non-callers before Rang is chosen). */
  myCards: Card[];
  toAct: Seat | null;
  legal: Card[];
  redeals: number;
  /** The viewer is the caller and may ask for a redeal. */
  canRedeal: boolean;
  /** Bandh Rang only: whether the hidden Rang is out, and whether the viewer may reveal it now. */
  hidden?: boolean;
  rangRevealed?: boolean;
  canReveal?: boolean;
}

export function handView(hand: HandState, viewer: Seat | null): HandView {
  const choosing = hand.phase === 'choosingTrump';
  const counts = ALL_SEATS.map((s) => (choosing ? (s === hand.caller ? FIRST_PACKET : 0) : hand.hands[s]!.length));
  let myCards: Card[] = [];
  if (viewer !== null) myCards = choosing ? (viewer === hand.caller ? hand.hands[viewer]!.slice(0, FIRST_PACKET) : []) : hand.hands[viewer]!.slice();
  return {
    dealer: hand.dealer,
    caller: hand.caller,
    direction: hand.direction,
    trump: hand.trump,
    phase: hand.phase,
    current: hand.current,
    tricks: hand.tricks,
    outcome: hand.outcome,
    counts,
    myCards,
    toAct: toAct(hand),
    legal: viewer === null ? [] : legalCards(hand, viewer),
    redeals: hand.redeals ?? 0,
    canRedeal: viewer === hand.caller && canAskRedeal(hand),
  };
}
